import React, { useCallback, useMemo } from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { identity } from 'ramda'

import { CheckBox as CheckedIcon, CheckBoxOutlineBlank as UncheckedIcon } from '@mui/icons-material'
import {
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
} from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { EMPTY_ARRAY, EMPTY_OBJECT, memoize } from '~/src/Lib/Utils'
import TextField from '~/src/UI/Shared/Form/TextField'
import { buildThemeFromColor } from '~/src/UI/Theme/mui'

const ALL_SELECTED = 'SIMPLE_SELECT_ALL_SELECTED'
const CLEAR_SELECTED = 'SIMPLE_SELECT_CLEAR_SELECTED'

const inputStyle = { height: '3rem', minHeight: '3rem' }

const getOptionStyle = memoize(color => (color ? { color } : EMPTY_OBJECT))

const displayName = 'SimpleSelect'

const styles = theme => ({
  root: {
    height: '3rem'
  },
  largeLabel: {
    '& $labelRoot, & $select': {
      ...theme.typography.h6,
      color: theme.palette.primary.main,
    },
  },
  labelRoot: {
    ...theme.typography.subtitle1,
    top: `${-theme.spacing(4.5)} !important`,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
  },
  colorPrimary: {
    color: theme.palette.primary.main,
    '& $select': {
      color: theme.palette.primary.main,
    },
  },
  item: {
    '& [class*=MuiListItemIcon-root]': {
      color: 'inherit',
    },
    '& + [class*=MuiListItemSecondaryAction-root]': {
      cursor: 'pointer',
    }
  },
  select: {
    ...theme.typography.h6,
    height: 36,
    width: '100%',
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(),
    fontSize: '0.875rem',
    '&:focus': {
      backgroundColor: 'transparent',
    }
  },
  selected: {
    '&:focus': {
      backgroundColor: `${theme.palette.primary.active} !important`,
    },
    '&:hover': {
      backgroundColor: `${theme.palette.hovered} !important`,
    },
  },
  selectIcon: {
    top: `calc(50% - ${theme.spacing(1.5)})`,
    right: 16,
    pointerEvents: 'none',
  },
  transparentInput: {
    '& .MuiInput-root:before': {
      background: 'transparent'
    }
  }
})

const useStyles = makeStyles(styles, { name: displayName })

/**
 * A simple select component that is styled like a text field.
 * Use ~/src/UI/Shared/Select instead.
 * @deprecated
 */
export const SimpleSelectComponent = ({
  classes: classesProp,
  clear = false,
  color = 'secondary',
  label: labelProp = '',
  multiple = false,
  options = EMPTY_ARRAY,
  selectAll = false,
  selectAllLabel = '',
  transparent = false,
  value = '',
  renderValue = identity,
  onChange: onChangeProp,
  ...rest
}) => {
  const classes = useStyles({ classes: classesProp })
  const label = typeof labelProp === 'function'
    ? labelProp({ options, value, ...rest })
    : labelProp
  const onChange = useCallback(newVal => {
    if (selectAll && newVal?.includes(ALL_SELECTED)) {
      onChangeProp(options.map(option => option.value))
      return
    }
    if (newVal === CLEAR_SELECTED || (multiple && newVal?.includes(CLEAR_SELECTED))) {
      onChangeProp(multiple ? EMPTY_ARRAY : null)
      return
    }
    onChangeProp(newVal)
  }, [multiple, onChangeProp, options, selectAll])

  const SelectProps = useMemo(() => ({
    classes: {
      root: color === 'primary' || color === 'danger' ? classes.colorPrimary : '',
      select: classes.select,
      icon: classes.selectIcon,
    },
    multiple,
    renderValue,
  }), [classes.colorPrimary, classes.select, classes.selectIcon, color, multiple, renderValue])

  let SelectAllIcon = null
  if (multiple && selectAll) {
    SelectAllIcon = options.every(option => value?.includes?.(option.value))
      ? CheckedIcon
      : UncheckedIcon
  }

  const selectContent = (
    <TextField
      data-testid="simple-select"
      margin="dense"
      {...rest}
      select
      autoComplete="off"
      classes={transparent ? { root: classes.transparentInput } : EMPTY_OBJECT}
      data-is-select="true"
      label={(multiple && value?.length) || (!multiple && value && renderValue) ? '' : label}
      value={!multiple || Array.isArray(value) ? value : EMPTY_ARRAY}
      onChange={onChange}
      InputProps={{
        disableUnderline: true,
        style: inputStyle,
      }}
      InputLabelProps={{
        shrink: false,
        classes: { root: classes.labelRoot },
      }}
      SelectProps={SelectProps}
    >
      {clear && ((multiple && value.length) || value) ? (
        <MenuItem
          data-testid="simple-select-clear"
          classes={{
            root: classNames({ [classes.colorPrimary]: color === 'primary', [classes.item]: true }),
            selected: multiple ? classes.selected : undefined,
          }}
          value={CLEAR_SELECTED}
        >
          <ListItemText>{i18n`Clear`}</ListItemText>
        </MenuItem>
      ) : null}
      {multiple && selectAll ? (
        <MenuItem
          data-testid="simple-select-select-all"
          disabled={options.every(option => value?.includes?.(option.value))}
          classes={{
            root: classNames({ [classes.colorPrimary]: color === 'primary', [classes.item]: true }),
            selected: multiple ? classes.selected : undefined,
          }}
          value={ALL_SELECTED}
        >
          <ListItemIcon data-testid="simple-select-icon">
            <SelectAllIcon color="primary" />
          </ListItemIcon>
          <ListItemText>{selectAllLabel || i18n`Select All`}</ListItemText>
        </MenuItem>
      ) : null}
      {options.map(option => {
        const style = option.color ? getOptionStyle(option.color) : undefined
        const { Icon } = option
        let CheckIcon
        let StateIcon
        if (multiple) {
          CheckIcon = value?.includes(option.value) ? CheckedIcon : UncheckedIcon
        }
        if (CheckIcon) {
          StateIcon = (
            <CheckIcon color={option.color ? undefined : 'primary'} />
          )
        }
        return (
          <MenuItem
            data-testid={`simple-select-menuItem-${option.value}`}
            classes={{
              root: classNames({ [classes.colorPrimary]: color === 'primary', [classes.item]: true }),
              selected: multiple ? classes.selected : undefined,
            }}
            disabled={!!option.disabled}
            key={option.value}
            value={option.value}
            style={style}
          >
            {multiple ? (
              [
                Icon || StateIcon ? (
                  <ListItemIcon data-testid="simple-select-icon" key={`${option.value}_icon`}>
                    {Icon ? <Icon color="inherit" /> : StateIcon }
                  </ListItemIcon>
                ) : null, (
                  <ListItemText disableTypography={typeof option.label !== 'string'} key={`${option.value}_label`}>
                    {option.label}
                  </ListItemText>
                ), Icon && StateIcon ? (
                  <ListItemSecondaryAction
                    key={`${option.value}_check`}
                    style={style}
                    onClick={() => {
                      let newValue = option.value
                      if (multiple) {
                        newValue = value.includes(option.value)
                          ? value.filter(v => v !== option.value)
                          : [...value, option.value]
                      }
                      onChange(newValue)
                    }}
                  >
                    {StateIcon}
                  </ListItemSecondaryAction>
                ) : null
              ]
            ) : (
              option.label
            )}
          </MenuItem>
        )
      })}
    </TextField>
  )

  return color === 'danger'
    ? (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme => buildThemeFromColor(theme.palette.error)}>{selectContent}</ThemeProvider>
      </StyledEngineProvider>
    )
    : selectContent
}

const valuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number])

SimpleSelectComponent.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary']),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  clear: PropTypes.bool,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: valuePropType.isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  onChange: PropTypes.func.isRequired,
  renderValue: PropTypes.func,
  selectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  transparent: PropTypes.bool,
  value: PropTypes.oneOfType([valuePropType, PropTypes.arrayOf(valuePropType)]),
}

SimpleSelectComponent.displayName = displayName

export default React.memo(SimpleSelectComponent)
